import React from "react";
import { Priceable } from "../../Types";
import { Quotation } from "../../../../stores/CoursePreviewStore/Types";
import AppliedVoucher from "./AppliedVoucher";
import PriceAmount, { CurrencyAmount } from "./PriceAmount";
import { savingPercent as savingPercentOf } from "../../service";

interface Props {
  isCourseError: boolean;
  priceable: Priceable;
  activeQuote: Quotation;
  monthlySubsequentPayment: string;
  couponCode: string;
  refCode: string;
  refreshQuoteWithoutCoupon: (e: any) => void;
}

const Course: React.FC<Props> = ({
  isCourseError = false,
  priceable,
  activeQuote,
  monthlySubsequentPayment,
  couponCode,
  refCode,
  refreshQuoteWithoutCoupon,
}) => {
  const installmentCount = activeQuote?.installment_count || 3;
  const remainingInstallments = Math.max(installmentCount - 1, 0);
  const courseTotalAmount =
    activeQuote?.formatted_total_amount || activeQuote?.formatted_amount;
  const subsequentPaymentAmount =
    monthlySubsequentPayment || activeQuote?.formatted_amount;
  const showFullPayment = activeQuote && !activeQuote.course_payment_plan;
  const showFullPaymentSaving = Boolean(
    showFullPayment &&
      activeQuote?.discounted &&
      activeQuote?.original_price?.formatted_amount,
  );
  // Worked out from what is actually being charged, not a literal 15%: the
  // usual case still comes to 15%, and a voucher that takes more says so.
  const savingPercent = savingPercentOf(activeQuote);

  return (
    <>
      <h3>Your plan</h3>
      {priceable ? (
        <>
          {/* The course's tile across the top of the card, as the course
              lists show it, rather than a square cut from its header. */}
          {priceable.tile_image ? (
            <img
              className="plan-photo plan-photo--tile"
              src={priceable.tile_image}
              alt={`${priceable.name} course`}
            />
          ) : null}
          <div className="plan-header">
            {!priceable.tile_image && (
              <div
                className="plan-icon"
                id="planIcon"
                aria-hidden="true"
                style={{
                  backgroundImage: `url(${priceable?.checkout_image})`,
                }}
              ></div>
            )}
            <div className="plan-details">
              <h4 id="planTitle">{priceable?.name}</h4>
              <p id="planSubtitle">
                {priceable?.number_of_lessons} lessons · CPD Certified
              </p>
            </div>
          </div>
          <div id="priceBreakdown">
            <div className="price-line">
              <span>Course</span>
              {activeQuote?.course_payment_plan ? (
                <span>
                  <strong>
                    <CurrencyAmount
                      quote={activeQuote}
                      amount={courseTotalAmount}
                    />
                  </strong>
                </span>
              ) : (
                <PriceAmount quote={activeQuote} />
              )}
            </div>

            {activeQuote?.course_payment_plan && (
              <div className="price-line">
                <span>Spread over</span>
                <span>{installmentCount} monthly payments</span>
              </div>
            )}

            {!priceable?.sample && (
              <div className="price-line">
                <span>Community Hub</span>
                <span>3 months included</span>
              </div>
            )}
            <AppliedVoucher
              couponCode={couponCode}
              refCode={refCode}
              refreshQuoteWithoutCoupon={refreshQuoteWithoutCoupon}
            />
            <div className="price-line total">
              <span>Today's charge</span>
              {activeQuote?.course_payment_plan ? (
                <div className="price-right">
                  <strong>
                    <CurrencyAmount
                      quote={activeQuote}
                      amount={activeQuote.formatted_amount}
                    />
                  </strong>
                  <div className="monthly-note">
                    then {remainingInstallments} ×{" "}
                    <CurrencyAmount
                      quote={activeQuote}
                      amount={subsequentPaymentAmount}
                    />
                  </div>
                </div>
              ) : showFullPayment ? (
                <div className="price-right">
                  <PriceAmount
                    quote={activeQuote}
                    showDiscountComparison={false}
                  />
                  <div className="discount-note">One-off, save</div>
                </div>
              ) : (
                <PriceAmount quote={activeQuote} />
              )}
            </div>
          </div>
        </>
      ) : (
        <div>
          <div className="title">
            <strong>No course selected</strong>
          </div>
          <div className="description">Please select a course!</div>
        </div>
      )}
      <div className="whats-included" id="whatsIncluded">
        <h4>What you get</h4>
        <ul>
          <li>Lifetime access to this course</li>
          <li>
            {priceable?.number_of_lessons} lessons with practical assignments
          </li>
          <li>CPD-accredited Mini MBA certificate on completion</li>
          {!priceable?.sample && <li>3 months of Community Hub access</li>}
        </ul>
        <div
          className="whats-included"
          style={{ marginTop: "12px", paddingTop: "12px" }}
        >
          <h4>Available with All Courses Pass</h4>
          <ul>
            <li className="not-included">
              Instant access to all 40+ courses (no per-course buying)
            </li>
            <li className="not-included">
              Community Hub for the full year, not just 3 months
            </li>
            <li className="not-included">
              Price locked in, never goes up while you stay subscribed
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default Course;
